import {
	AlertBar,
	CheckboxGroup,
	CheckboxGroupItem,
	Form,
	FormItem,
	SecretValueInput,
	TextInput,
	ToolbarAction,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	EMPTY_ARRAY,
	ensureNotNil,
	isNotNil,
	isNotNilOrEmpty
} from "client/Misc/utils";
import { tryParseInt } from "client/Misc/parseUtils";
import {
	IValidationConfiguration,
	createValidationConfiguration,
	InputValidationResultKind,
	InputValidationResult,
	IRootValidation
} from "client/Validation";
import {
	inline,
	isEmailAddress,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	AlreadyConfiguredEnvironmentAppLinkWithCredentialName,
	AlreadyConfiguredIntegrationFlowAuthenticationWithCredentialName,
	AlreadyConfiguredLinks,
	create,
	CreateIntegrationCredentialsSaveModel,
	CreateOrEditIntegrationCredentialsModel,
	EnvironmentAppListItem,
	getViewModelForEditCredential,
	getViewModelForNewCredential,
	LinksViewModel,
	update,
	UpdateIntegrationCredentialsSaveModel
} from "client/Api/IntegrationCredentials/integrationCredentials";
import { SecretValue } from "client/Api/Apps/common";
import { ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { isEmailAddress as checkIsEmailAddress } from "client_legacy/ndaw/stringFormatValidator";
import {
	FunctionComponent,
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { SettingsPage } from "../SettingsPage";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import { createLinkLabel, LinkFormItem } from "./LinkFormItem";
import { useUnlinkAnotherCredentialsConfirmationDialog } from "./UnlinkAnotherCredentialsConfirmationDialog";
import { integrationCredentialsRouting } from "./OverviewPage";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import {
	NavigationCallback,
	PredefinedPropOptionKind,
	RouteParametersFromProps,
	connectToRouter
} from "client/Navigation";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export type EditIntegrationCredentialsPageProps = {
	credentialId?: number;
	onNavigate: NavigationCallback;
};

export const EditIntegrationCredentialsPage = (props: EditIntegrationCredentialsPageProps): ReactElement | null => {
	const {
		credentialId,
		onNavigate
	} = props;

	useActiveSettingsMenuItem(SettingsPageKind.IntegrationCredentials);

	const [isBusy, setIsBusy] = useState<boolean>(false);

	const [formData, setFormData] = useState<UpdateIntegrationCredentialsSaveModel>(defaultFormData);
	const [viewModel, setViewModel] = useState<ViewModel>({
		alreadyConfiguredLinks: defaultAlreadyConfiguredLinks,
		availableLinks: defaultAvailableLinks,
		credentialId: credentialId ?? null,
		isEditingExistingCredentials: isNotNil(credentialId),
		passwordContainsValue: false
	});
	const [shouldShowUsernameAlert, setShouldShowUsernameAlert] = useState<boolean>(false);
	const [isUsernameAlertDismissed, setIsUsernameAlertDismissed] = useState<boolean>(false);

	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const alertManager: AlertManager = useAlerts();

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);
					const {
						model,
						availableLinks,
						alreadyConfiguredLinks,
						isEditingExistingCredentials
					} = isNotNil(credentialId)
						? await getViewModelForEditCredential(credentialId, signal)
							.catch(handlePageDataLoadingError)
						: await getViewModelForNewCredential(signal)
							.catch(handlePageDataLoadingError);

					setIsBusy(false);
					setFormData(convertToSaveModel(model));
					setViewModel({
						alreadyConfiguredLinks,
						availableLinks,
						credentialId: model.id,
						isEditingExistingCredentials,
						passwordContainsValue: model.passwordContainsValue
					});
					setShouldShowUsernameAlert(checkIfShouldShowUsernameAlert(model.username));
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					setIsBusy(false);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}

			const cancellation = new AbortController();

			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager, credentialId]
	);

	const {
		alreadyConfiguredLinks,
		availableLinks,
		isEditingExistingCredentials,
		passwordContainsValue
	} = viewModel;

	const validationConfiguration: IValidationConfiguration<UpdateIntegrationCredentialsSaveModel> = useValidationConfiguration(
		availableLinks,
		!isEditingExistingCredentials
	);

	const formHandler: IFormHandler<UpdateIntegrationCredentialsSaveModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const [unlinkAnotherConfirmationDialog, openUnlinkAnotherConfirmationDialog] = useUnlinkAnotherCredentialsConfirmationDialog();

	const isUsernameInUpnFormat = useMemo(
		(): boolean => {
			return checkIsEmailAddress(formData.username);
		},
		[formData.username]
	);

	const canLinkBeEnabled = useCallback(
		(doesSupportNonUpnUsername: boolean): boolean => {
			return isUsernameInUpnFormat || doesSupportNonUpnUsername;
		},
		[isUsernameInUpnFormat]
	);

	const environmentAppOptions = useMemo(
		(): readonly CheckboxGroupItem<number>[] => {
			const isAppSelected = (id: number): boolean => {
				return formData.links
					.environmentApps
					.some(a => a === id);
			};

			return availableLinks.environmentApps
				.map(
					(availableApp: EnvironmentAppListItem): CheckboxGroupItem<number> => {
						const alreadyConfiguredLink: AlreadyConfiguredEnvironmentAppLinkWithCredentialName | undefined = alreadyConfiguredLinks
							.environmentApps
							.find(l => l.id === availableApp.id);

						const isEnabled: boolean = canLinkBeEnabled(availableApp.doesSupportNonUpnUsername)
							|| isAppSelected(availableApp.id);

						return {
							value: availableApp.id,
							text: createLinkLabel(availableApp.name, alreadyConfiguredLink?.credentialUsername),
							isDisabled: !isEnabled,
							async canChange(newValue: boolean): Promise<boolean> {
								if (!newValue) {
									return true;
								}

								if (!alreadyConfiguredLink) {
									return true;
								}

								const result: TryResult<void> = await openUnlinkAnotherConfirmationDialog({
									credentialTypeName: availableApp.name,
									configuredByOtherCredentialName: alreadyConfiguredLink.credentialUsername
								});

								return result.wasSuccessful;
							}
						};
					}
				);
		},
		[
			alreadyConfiguredLinks.environmentApps,
			availableLinks.environmentApps,
			formData.links.environmentApps,
			canLinkBeEnabled,
			openUnlinkAnotherConfirmationDialog
		]
	);

	const integrationFlowAuthenticationOptions = useMemo(
		(): readonly CheckboxGroupItem<number>[] => {
			const isAuthenticationSelected = (id: number): boolean => {
				return formData.links
					.integrationFlowAuthentications
					.some(a => a === id);
			};

			return availableLinks.integrationFlowAuthentications
				.map(
					(authentication): CheckboxGroupItem<number> => {
						const alreadyConfiguredLink: AlreadyConfiguredIntegrationFlowAuthenticationWithCredentialName | undefined = alreadyConfiguredLinks
							.integrationFlowAuthentications
							.find((x) => x.id === authentication.id);

						const isEnabled: boolean = canLinkBeEnabled(authentication.doesSupportNonUpnUsername)
							|| isAuthenticationSelected(authentication.id);

						return {
							value: authentication.id,
							text: createLinkLabel(authentication.name, alreadyConfiguredLink?.credentialUsername),
							isDisabled: !isEnabled,
							async canChange(newValue: boolean): Promise<boolean> {
								if (!newValue) {
									return true;
								}

								if (!alreadyConfiguredLink) {
									return true;
								}

								const result: TryResult<void> = await openUnlinkAnotherConfirmationDialog({
									credentialTypeName: authentication.name,
									configuredByOtherCredentialName: alreadyConfiguredLink.credentialUsername
								});

								return result.wasSuccessful;
							}
						};
					}
				);
		},
		[
			alreadyConfiguredLinks.integrationFlowAuthentications,
			availableLinks.integrationFlowAuthentications,
			formData.links.integrationFlowAuthentications,
			canLinkBeEnabled,
			openUnlinkAnotherConfirmationDialog
		]
	);

	const hasAnyIntegrationsAvailable = useMemo(
		(): boolean => {
			const {
				exchange,
				environmentApps,
				fileServers,
				integrationFlowAuthentications
			} = availableLinks;

			return exchange.isAvailable
				|| fileServers.isAvailable
				|| environmentApps.length > 0
				|| integrationFlowAuthentications.length > 0;
		},
		[availableLinks]
	);

	const navigateToOverviewHandler = useCallback(
		(): void => {
			onNavigate(integrationCredentialsRouting.overview);
		},
		[onNavigate]
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			const validation: IRootValidation<UpdateIntegrationCredentialsSaveModel> = formHandler.validation;

			if (isBusy) {
				return;
			}

			if (!hasAnyIntegrationsAvailable) {
				alertManager.showError(localize("_CannotSaveCredentialsBecause"));
				return;
			}

			const {
				links
			} = formData;

			const containsAtLeastOneLink: boolean = links.exchange
				|| links.fileServers
				|| links.environmentApps.length > 0
				|| links.integrationFlowAuthentications.length > 0;

			if (!containsAtLeastOneLink) {
				alertManager.showError(localize("_PleaseSelectAtLeastOneItem"));
				return;
			}

			setIsBusy(true);

			const validationResult: InputValidationResult = await validation.validate();

			if (validationResult.kind === InputValidationResultKind.Invalid) {
				setIsBusy(false);
				return;
			}

			try {
				if (isEditingExistingCredentials) {
					await update(
						ensureNotNil(viewModel.credentialId),
						formData
					);
				} else {
					await create(
						prepareFormDataForCreation(formData)
					);
				}
				setIsBusy(false);
				navigateToOverviewHandler();
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}
				setIsBusy(false);
				if (validation.tryProcessServerError(e)) {
					return;
				}

				alertManager.showError(localize("_ChangesNotSaved"));
			}
		},
		[
			alertManager,
			formData,
			formHandler.validation,
			isBusy,
			isEditingExistingCredentials,
			viewModel.credentialId,
			hasAnyIntegrationsAvailable,
			navigateToOverviewHandler
		]
	);

	const defaultActions = useMemo(
		(): readonly ToolbarAction<undefined>[] => {
			return listDefaultActions(submitHandler, navigateToOverviewHandler);
		},
		[submitHandler, navigateToOverviewHandler]
	);

	const topBarActions = useMemo(
		(): readonly ToolbarAction<undefined>[] => {
			return buildActionsForTopBar<undefined, ToolbarAction<undefined>>(
				{
					defaultActions,
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive
		]
	);

	const usernameBlurHandler = useCallback(
		(newValue: string): void => {
			setShouldShowUsernameAlert(checkIfShouldShowUsernameAlert(newValue));
		},
		[]
	);

	return (
		<SettingsPage
			hasBusyIndicator={isBusy}
			onBack={navigateToOverviewHandler}
			actions={topBarActions}
			actionContext={undefined}
			title={localize("_CredentialsProperties")}
		>
			<Form onSubmit="NONE" hasWidthConstrained>
				<FormItem label={localize("_Username")}>
					<TextInput
						{...formHandler.for("username")
							.attachToTextInput()
							.withOnBlur(usernameBlurHandler)
						}
					/>
					<ValidationError
						{...formHandler.for("username")
							.attachToValidationError()
						}
					/>
				</FormItem>
				{shouldShowUsernameAlert && !isUsernameAlertDismissed && (
					<AlertBar
						kind="info"
						onDismiss={() => setIsUsernameAlertDismissed(true)}
						isInline
						trackingId="username-is-not-upn-alert"
					>
						{localize("_NonUpnUsernamesUsageInfo")}
					</AlertBar>
				)}
				<FormItem label={localize("_Password")}>
					<SecretValueInput
						{...formHandler.for("password")
							.attachToSecretValueInput()
						}
						containsInitialValue={passwordContainsValue}
					/>
					<ValidationError
						{...formHandler.for("password")
							.attachToValidationError(true)
						}
					/>
					<ValidationError
						{...formHandler.for("password")
							.for("value")
							.attachToValidationError()
						}
					/>
				</FormItem>
				<FormItem label={localize("_ConfirmPassword")}>
					<SecretValueInput
						{...formHandler.for("confirmPassword")
							.attachToSecretValueInput()
						}
						containsInitialValue={passwordContainsValue}
					/>
					<ValidationError
						{...formHandler.for("confirmPassword")
							.attachToValidationError(true)
						}
					/>
					<ValidationError
						{...formHandler.for("confirmPassword")
							.for("value")
							.attachToValidationError()
						}
					/>
				</FormItem>
				<h3>{localize("_UseCredentialsFor")}</h3>
				{availableLinks.exchange.isAvailable && (
					<>
						<h4>{localize("_Exchange")}</h4>
						<LinkFormItem
							attachable={
								formHandler.for("links")
									.for("exchange")
							}
							canBeEnabled={
								canLinkBeEnabled(
									availableLinks.exchange
										.doesSupportNonUpnUsername
								)
							}
							text={localize("_ExchangeConnectivity")}
							configuredByOtherCredentialName={alreadyConfiguredLinks.exchangeLogin}
						/>
					</>
				)}
				{availableLinks.fileServers.isAvailable && (
					<>
						<h4>{localize("_FileServers")}</h4>
						<LinkFormItem
							attachable={
								formHandler.for("links")
									.for("fileServers")
							}
							canBeEnabled={
								canLinkBeEnabled(
									availableLinks.fileServers
										.doesSupportNonUpnUsername
								)
							}
							text={localize("_AllFileServers")}
							configuredByOtherCredentialName={alreadyConfiguredLinks.fileServersLogin}
						/>
					</>
				)}
				{availableLinks.environmentApps.length > 0 && (
					<FormItem
						label={localize("_ClientlessRDPApps")}
						isForGroup
					>
						<CheckboxGroup
							{...formHandler.for("links")
								.for("environmentApps")
								.attachToCheckboxGroup()
							}
							options={environmentAppOptions}
						/>
						<ValidationError
							{...formHandler.for("links")
								.for("environmentApps")
								.attachToValidationError()
							}
						/>
					</FormItem>
				)}

				{integrationFlowAuthenticationOptions.length > 0 && (
					<FormItem
						label={{
							template: localize("_TextWithClarificationFormat"),
							replacements: [
								{
									what: "{{Text}}",
									filler: localize("_IntegrationAuthentications")
								},
								{
									what: "{{Clarification}}",
									filler: localize("_IntegrationBuilder")
										.toLowerCase()
								}
							]
						}}
						isForGroup
					>
						<CheckboxGroup
							{...formHandler.for("links")
								.for("integrationFlowAuthentications")
								.attachToCheckboxGroup()
							}
							options={integrationFlowAuthenticationOptions}
						/>
						<ValidationError
							{...formHandler.for("links")
								.for("integrationFlowAuthentications")
								.attachToValidationError()
							}
						/>
					</FormItem>
				)}

				{!hasAnyIntegrationsAvailable && (
					<p>{localize("_ThereAreNoAvailableAppsAndIntegrationsToLinkTheCredentialsTo")}</p>
				)}
				{unlinkAnotherConfirmationDialog}
			</Form>
			<AiAssistantFabSpacing />
		</SettingsPage>
	);
};
EditIntegrationCredentialsPage.displayName = "EditIntegrationCredentialsPage";

export type EditIntegrationCredentialsPageParams = RouteParametersFromProps<
	EditIntegrationCredentialsPageProps,
	"credentialId"
>;

export const EditIntegrationCredentialsPageForRouter: FunctionComponent = connectToRouter(
	EditIntegrationCredentialsPage,
	{
		"credentialId": {
			isRequired: false,
			tryParse: tryParseInt
		},
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

function useValidationConfiguration(
	availableLinksInfo: LinksViewModel,
	doesRequirePassword: boolean
): IValidationConfiguration<UpdateIntegrationCredentialsSaveModel> {
	const {
		environmentApps,
		exchange,
		fileServers,
		integrationFlowAuthentications
	} = availableLinksInfo;

	return useMemo(
		(): IValidationConfiguration<UpdateIntegrationCredentialsSaveModel> => {
			const appIdsWhichRequireUsernameInUpnFormat = new Set<number>(
				environmentApps.filter(a => !a.doesSupportNonUpnUsername)
					.map(a => a.id)
			);

			const authenticationIdsWhichRequireUsernameInUpnFormat = new Set<number>(
				integrationFlowAuthentications.filter(a => !a.doesSupportNonUpnUsername)
					.map(a => a.id)
			);

			const result: IValidationConfiguration<UpdateIntegrationCredentialsSaveModel>
				= createValidationConfiguration<UpdateIntegrationCredentialsSaveModel>();

			result.forScalar("username")
				.add(
					isNotNullNorUndefined(),
					isNotEmpty()
				);

			result.when(
				m => m.links.exchange && !exchange.doesSupportNonUpnUsername
					|| m.links.fileServers && !fileServers.doesSupportNonUpnUsername
					|| m.links.environmentApps
						.some(a => appIdsWhichRequireUsernameInUpnFormat.has(a))
					|| m.links.integrationFlowAuthentications
						.some((x) => authenticationIdsWhichRequireUsernameInUpnFormat.has(x))
				)
				.forScalar("username")
				.add(
					isEmailAddress(localize("_UserNameMustBeInUpnFormatForClientlessRdpAndExchange"))
				);

			result.forObject("password")
				.when(p => p.containsNewValue || doesRequirePassword)
				.forScalar("value")
				.add(
					isNotNullNorUndefined()
						.fixErrorOn("anyInput")
						.validateOn("anyInputFinished"),
					isNotEmpty()
						.fixErrorOn("anyInput")
						.validateOn("anyInputFinished")
				);

			result.when(
				m => m.password.containsNewValue
					|| m.confirmPassword.containsNewValue
					|| doesRequirePassword
			)
				.forObject("confirmPassword")
				.add(
					inline(
						(confirmPassword, validationContext) => {
							const parent = validationContext.parentValue as UpdateIntegrationCredentialsSaveModel;
							const password: SecretValue = parent.password;

							if (confirmPassword.value === password.value) {
								return {
									kind: InputValidationResultKind.Valid
								};
							}

							return {
								kind: InputValidationResultKind.Invalid,
								errorMessage: localize("_ValidationPasswordConfirm")
							};
						}
					)
				);

			result.forObject("links")
				.add(
					inline(
						(links) => {
							const containsAtLeastOneLink: boolean = links.exchange
								|| links.fileServers
								|| links.environmentApps.length > 0
								|| links.integrationFlowAuthentications.length > 0;

							if (containsAtLeastOneLink) {
								return {
									kind: InputValidationResultKind.Valid
								};
							}

							return {
								kind: InputValidationResultKind.Invalid,
								errorMessage: localize("_PleaseSelectAtLeastOneItem")
							};
						}
					)
				);

			return result;
		},
		[
			doesRequirePassword,
			environmentApps,
			exchange.doesSupportNonUpnUsername,
			fileServers.doesSupportNonUpnUsername,
			integrationFlowAuthentications
		]
	);
};

function prepareFormDataForCreation(
	formData: UpdateIntegrationCredentialsSaveModel
): CreateIntegrationCredentialsSaveModel {
	return {
		...formData,
		password: ensureNotNil(
			formData.password
				.value
		),
		confirmPassword: ensureNotNil(
			formData.confirmPassword
				.value
		)
	};
}

function convertToSaveModel(
	model: CreateOrEditIntegrationCredentialsModel
): UpdateIntegrationCredentialsSaveModel {
	return {
		username: model.username,
		password: {
			containsNewValue: false,
			value: null
		},
		confirmPassword: {
			containsNewValue: false,
			value: null
		},
		links: model.links
	};
}

function listDefaultActions(
	submitHandler: () => void,
	navigateToOverviewHandler: () => void
): readonly ToolbarAction<undefined>[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "save",
			title: localize("_Save"),
			icon: "floppy-disk",
			canExecute: () => true,
			execute: submitHandler,
			isPrimary: true
		},
		{
			kind: ActionKind.Regular,
			name: "cancel",
			title: localize("_Cancel"),
			icon: "xmark",
			canExecute: () => true,
			execute: navigateToOverviewHandler
		}
	];
}

function checkIfShouldShowUsernameAlert(username: string): boolean {
	const isUsernameInUpnFormat: boolean = checkIsEmailAddress(username);

	return isNotNilOrEmpty(username) && !isUsernameInUpnFormat;
}

const defaultAvailableLinks: LinksViewModel = {
	fileServers: {
		isAvailable: false,
		doesSupportNonUpnUsername: false
	},
	exchange: {
		isAvailable: false,
		doesSupportNonUpnUsername: false
	},
	environmentApps: EMPTY_ARRAY,
	integrationFlowAuthentications: EMPTY_ARRAY
};

const defaultAlreadyConfiguredLinks: AlreadyConfiguredLinks = {
	exchangeLogin: null,
	fileServersLogin: null,
	environmentApps: EMPTY_ARRAY,
	integrationFlowAuthentications: EMPTY_ARRAY
};

const defaultFormData: UpdateIntegrationCredentialsSaveModel = {
	username: "",
	password: {
		value: "",
		containsNewValue: false
	},
	confirmPassword: {
		value: "",
		containsNewValue: false
	},
	links: {
		fileServers: false,
		exchange: false,
		environmentApps: EMPTY_ARRAY,
		integrationFlowAuthentications: EMPTY_ARRAY
	}
};

type ViewModel = {
	availableLinks: LinksViewModel;
	alreadyConfiguredLinks: AlreadyConfiguredLinks;
	isEditingExistingCredentials: boolean;
	passwordContainsValue: boolean;
	credentialId: number | null;
};
